import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { toast } from "react-toastify";

const initialState = {
	list: [],
	loading: false,
	error: null,
};

const extractMessage = (payload, fallback) => {
	const message =
		payload?.message ??
		payload?.Message ??
		payload?.statusMessage ??
		payload?.StatusMessage ??
		payload?.Status ??
		payload?.status;
	return typeof message === "string" && message.trim() ? message : fallback;
};

export const loadExpenseTypes = createAsyncThunk(
	"expenseType/loadAll",
	async (_, { rejectWithValue }) => {
		try {
			const { data } = await axios.get("expenses/ExpanseTypeGetbyCompanyId");
			return Array.isArray(data) ? data : [];
		} catch (error) {
			return rejectWithValue(
				error?.response?.data ?? {
					message: error?.message || "Unable to load expense types",
				}
			);
		}
	}
);

export const saveExpenseType = createAsyncThunk(
	"expenseType/saveSingle",
	async (payload, { rejectWithValue }) => {
		const { ExpanseTypeId } = payload || {};
		const normalizedPayload = {
			ExpanseTypeId: ExpanseTypeId ?? 0,
			ExpanseTypeName: payload?.ExpanseTypeName ?? "",
			ExpanseTypeDescription: payload?.ExpanseTypeDescription ?? null,
			CompanyId: payload?.CompanyId ?? null,
			DataStatus: payload?.DataStatus ?? 1,
		};
		try {
			const { data } = await axios.post("expenses/ExpanseTypeSetup", normalizedPayload);
			toast.success(
				extractMessage(
					data,
					ExpanseTypeId && ExpanseTypeId !== 0
						? "Expense type updated successfully"
						: "Expense type added successfully"
				)
			);
			return data;
		} catch (error) {
			const responseData = error?.response?.data;
			toast.error(
				extractMessage(
					responseData,
					"Failed to save expense type"
				)
			);
			return rejectWithValue(
				responseData ?? {
					message: error?.message || "Unable to save expense type",
				}
			);
		}
	}
);

export const deleteExpenseType = createAsyncThunk(
	"expenseType/deleteSingle",
	async (payload, { rejectWithValue }) => {
		const { ExpanseTypeId } = payload || {};
		if (!ExpanseTypeId) {
			return rejectWithValue({ message: "Invalid expense type id" });
		}
		try {
			const { data } = await axios.post("expenses/ExpanseTypeSetup", {
				ExpanseTypeId,
				DataStatus: 2,
			});
			toast.success(extractMessage(data, "Expense type deleted successfully"));
			return {
				data,
				ExpanseTypeId,
			};
		} catch (error) {
			const responseData = error?.response?.data;
			toast.error(
				extractMessage(
					responseData,
					"Failed to delete expense type"
				)
			);
			return rejectWithValue(
				responseData ?? {
					message: error?.message || "Unable to delete expense type",
				}
			);
		}
	}
);

const expenseTypeSlice = createSlice({
	name: "expenseType",
	initialState,
	reducers: {},
	extraReducers: (builder) => {
		builder
			.addCase(loadExpenseTypes.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(loadExpenseTypes.fulfilled, (state, action) => {
				state.loading = false;
				state.list = action.payload ?? [];
			})
			.addCase(loadExpenseTypes.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload?.message || "Unable to load expense types";
			})
			.addCase(saveExpenseType.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(saveExpenseType.fulfilled, (state) => {
				state.loading = false;
			})
			.addCase(saveExpenseType.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload?.message || "Unable to save expense type";
			})
			.addCase(deleteExpenseType.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(deleteExpenseType.fulfilled, (state, action) => {
				state.loading = false;
				const deletedId = action.payload?.ExpanseTypeId;
				if (deletedId) {
					state.list = state.list.filter(
						(item) => Number(item.ExpanseTypeId) !== Number(deletedId)
					);
				}
			})
			.addCase(deleteExpenseType.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload?.message || "Unable to delete expense type";
			});
	},
});

export default expenseTypeSlice.reducer;

